import { useState } from 'react';

interface FrameworkIntroProps {
  onStart: () => void;
}

export function FrameworkIntro({ onStart }: FrameworkIntroProps) {
  const [showPrivacyDetails, setShowPrivacyDetails] = useState(false);

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4">
      <div className="max-w-3xl mx-auto space-y-8">
        {/* 标题 */}
        <header className="text-center">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">MindType</h1>
          <p className="text-lg text-gray-600">认知风格测评</p>
        </header>

        {/* 引言 */}
        <section className="bg-white rounded-xl border border-blue-100 p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">什么是认知风格？</h2>
          <p className="text-gray-700 leading-relaxed">
            认知风格是你处理信息、做出判断、应对环境时的自然倾向。它不是能力的高低，而是方式的不同。了解自己的认知风格，可以帮助你理解自己在不同情境下的反应模式，找到更适合自己的环境和工作方式。
          </p>
        </section>

        {/* 四维度介绍 */}
        <section className="bg-white rounded-xl border border-blue-100 p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-6">四个核心维度</h2>

          <div className="space-y-6">
            {/* D1 锚定来源 */}
            <div className="border-l-4 border-blue-500 pl-4">
              <h3 className="font-semibold text-gray-900 mb-2">
                D1 锚定来源
                <span className="text-sm font-normal text-gray-500 ml-2">你做判断时的参照标准</span>
              </h3>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div className="bg-blue-50 rounded-lg p-3">
                  <span className="font-medium text-blue-700">A 内部锚定</span>
                  <p className="text-gray-600 mt-1">依据自己的标准评价事物，不轻易被外界动摇</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <span className="font-medium text-gray-700">R 外部锚定</span>
                  <p className="text-gray-600 mt-1">参考外部反馈和标准，善于适应环境期待</p>
                </div>
              </div>
            </div>

            {/* D2 处理模式 */}
            <div className="border-l-4 border-blue-500 pl-4">
              <h3 className="font-semibold text-gray-900 mb-2">
                D2 处理模式
                <span className="text-sm font-normal text-gray-500 ml-2">你处理信息的方式</span>
              </h3>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div className="bg-blue-50 rounded-lg p-3">
                  <span className="font-medium text-blue-700">S 系统化</span>
                  <p className="text-gray-600 mt-1">结构化、逻辑化地分析问题，建立框架</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <span className="font-medium text-gray-700">C 情境化</span>
                  <p className="text-gray-600 mt-1">灵活应变、依靠直觉，根据具体情况调整</p>
                </div>
              </div>
            </div>

            {/* D3 驱动来源 */}
            <div className="border-l-4 border-blue-500 pl-4">
              <h3 className="font-semibold text-gray-900 mb-2">
                D3 驱动来源
                <span className="text-sm font-normal text-gray-500 ml-2">驱动你行动的动力</span>
              </h3>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div className="bg-blue-50 rounded-lg p-3">
                  <span className="font-medium text-blue-700">I 内在驱动</span>
                  <p className="text-gray-600 mt-1">由兴趣、好奇心和意义感驱动</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <span className="font-medium text-gray-700">E 外在驱动</span>
                  <p className="text-gray-600 mt-1">由结果、认可和外部回报驱动</p>
                </div>
              </div>
            </div>

            {/* D4 不确定性态度 */}
            <div className="border-l-4 border-blue-500 pl-4">
              <h3 className="font-semibold text-gray-900 mb-2">
                D4 不确定性态度
                <span className="text-sm font-normal text-gray-500 ml-2">你面对不确定性的倾向</span>
              </h3>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div className="bg-blue-50 rounded-lg p-3">
                  <span className="font-medium text-blue-700">D 闭合取向</span>
                  <p className="text-gray-600 mt-1">倾向于尽快明确方向，减少不确定性</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <span className="font-medium text-gray-700">O 探索取向</span>
                  <p className="text-gray-600 mt-1">愿意保持开放，享受探索的过程</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* 三层情境 */}
        <section className="bg-white rounded-xl border border-blue-100 p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">三层情境测量</h2>
          <p className="text-gray-700 mb-4">
            人的认知风格并非一成不变。本测评会分别测量你在三种不同情境下的表现：
          </p>
          <div className="grid grid-cols-3 gap-4">
            <div className="text-center p-4 bg-blue-50 rounded-lg">
              <div className="w-10 h-10 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-lg font-medium mx-auto mb-3">
                1
              </div>
              <h4 className="font-medium text-gray-900">日常模式</h4>
              <p className="text-sm text-gray-600 mt-1">放松、自主的状态</p>
            </div>
            <div className="text-center p-4 bg-blue-50 rounded-lg">
              <div className="w-10 h-10 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-lg font-medium mx-auto mb-3">
                2
              </div>
              <h4 className="font-medium text-gray-900">工作模式</h4>
              <p className="text-sm text-gray-600 mt-1">有任务、有要求的环境</p>
            </div>
            <div className="text-center p-4 bg-blue-50 rounded-lg">
              <div className="w-10 h-10 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-lg font-medium mx-auto mb-3">
                3
              </div>
              <h4 className="font-medium text-gray-900">压力模式</h4>
              <p className="text-sm text-gray-600 mt-1">紧迫、高压的情境</p>
            </div>
          </div>
        </section>

        {/* 结果价值 */}
        <section className="bg-white rounded-xl border border-blue-100 p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">测评结果能告诉你什么？</h2>
          <ul className="space-y-3 text-gray-700">
            <li className="flex items-start gap-3">
              <span className="text-blue-500 mt-1">•</span>
              <span>你在不同情境下的认知风格类型（16种类型之一）</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="text-blue-500 mt-1">•</span>
              <span>哪些维度是你的稳定特质，哪些会随情境变化</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="text-blue-500 mt-1">•</span>
              <span>什么样的环境更适合你，什么样的环境可能消耗你</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="text-blue-500 mt-1">•</span>
              <span>你的优势、潜在挑战和成长建议</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="text-blue-500 mt-1">•</span>
              <span>你与他人产生分歧时，可能的认知差异根源和相处建议</span>
            </li>
          </ul>
        </section>

        {/* 测试说明 */}
        <section className="bg-blue-50 rounded-xl border border-blue-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-3">开始之前</h2>
          <ul className="space-y-2 text-gray-700 text-sm">
            <li>• 共 77 道题目，约需 10-15 分钟</li>
            <li>• 没有对错之分，选择更符合你真实想法的选项</li>
            <li>• 答案会自动保存，中途离开可以继续</li>
            <li>• 建议在安静、不被打扰的环境下完成</li>
          </ul>
        </section>

        {/* 数据隐私声明 */}
        <section className="bg-gray-50 rounded-xl border border-gray-200 p-5">
          <div className="flex items-start gap-3">
            <div className="text-gray-400 mt-0.5">
              <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
            </div>
            <div className="flex-1">
              <h3 className="text-sm font-medium text-gray-700 mb-2">数据说明</h3>
              <p className="text-sm text-gray-600 leading-relaxed">
                本测试会收集您的答题数据和测试结果，用于生成个人报告及匿名统计分析。
                我们不收集任何个人身份信息。
              </p>

              {/* 展开详情按钮 */}
              <button
                onClick={() => setShowPrivacyDetails(!showPrivacyDetails)}
                className="text-sm text-blue-600 hover:text-blue-700 mt-2 flex items-center gap-1"
              >
                {showPrivacyDetails ? '收起详情' : '了解更多'}
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className={`w-4 h-4 transition-transform ${showPrivacyDetails ? 'rotate-180' : ''}`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {/* 详细说明（可展开） */}
              {showPrivacyDetails && (
                <div className="mt-4 pt-4 border-t border-gray-200 space-y-3 text-sm text-gray-600">
                  <div>
                    <h4 className="font-medium text-gray-700 mb-1">数据收集范围</h4>
                    <ul className="space-y-1">
                      <li className="flex items-start gap-2">
                        <span className="text-gray-400">•</span>
                        <span>测试答案和结果类型</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-gray-400">•</span>
                        <span>反馈意见（如您选择提交）</span>
                      </li>
                    </ul>
                  </div>

                  <div>
                    <h4 className="font-medium text-gray-700 mb-1">数据用途</h4>
                    <ul className="space-y-1">
                      <li className="flex items-start gap-2">
                        <span className="text-gray-400">•</span>
                        <span>生成您的测试报告</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-gray-400">•</span>
                        <span>匿名统计分析，用于验证和改进测试框架</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-gray-400">•</span>
                        <span>聚合统计数据可能公开展示</span>
                      </li>
                    </ul>
                  </div>

                  <div>
                    <h4 className="font-medium text-gray-700 mb-1">我们承诺</h4>
                    <ul className="space-y-1">
                      <li className="flex items-start gap-2">
                        <span className="text-gray-400">•</span>
                        <span>不收集姓名、邮箱等个人身份信息</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-gray-400">•</span>
                        <span>不会将个体数据单独公开</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-gray-400">•</span>
                        <span>您可通过反馈渠道要求删除数据</span>
                      </li>
                    </ul>
                  </div>

                  <p className="text-gray-500 pt-2">
                    继续测试即表示您同意以上数据使用方式。
                  </p>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* 开始按钮 */}
        <div className="text-center pt-4">
          <button
            onClick={onStart}
            className="px-12 py-4 bg-blue-600 text-white text-lg font-medium rounded-xl hover:bg-blue-700 transition-colors shadow-lg"
          >
            开始测试
          </button>
        </div>

        {/* 版权声明 */}
        <footer className="text-center text-xs text-gray-400 py-6 mt-8">
          <p>© 2024 MindType</p>
          <p className="mt-1">内容受版权保护，未经授权禁止商业使用</p>
        </footer>
      </div>
    </div>
  );
}
